import { PollingStatus } from "@/types/PollingStatus";
import { MutationTree } from "vuex";
import {
	SiteMonitorState,
	EventRecordFilter,
	ApplianceServerDetails,
	DeviceServerDetails,
	ExtraDetailsExpandedState,
	NotifyNotificationRules,
	InvolvedParty,
	ForceRestoreModalData
} from "./types";
import { AuditService } from "@/services/auditService";
import { formatDateMixin } from "@/mixins";
import { EventRecord, MaskReason, MaskedAlarm, ShareWithPerson } from "./types";
import { getDefaultState } from "./state";
import Vue from "vue";
import api from "@/services/api.service";
import EventRecordTypes from "@/types/sv-data/enums/EventRecordTypes";
import { EventOutcomeResponse } from "@/types/EventOutcome";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";

const { dateStringWithZeroHourOffset } = formatDateMixin.methods;
const largeEventSize: number = 150; // Number of eventRecords until an event is considered "Large"

export const mutations: MutationTree<SiteMonitorState> = {
	setAreaNotesVisible(state, isVisible: boolean) {
		state.AreaNotesVisible = isVisible;
	},
	resetEventRecords(state) {
		state.EventRecords = [];
		state.LastAckAllEventRecordID = 0;
		state.LatestEventRecordID = 0;
		state.AdhocLocationEventRecords = [];
		state.ActiveResponseIDs = {};

		state.EventRecordsAwaitingAck = {};
		state.EventRecordsAwaitingAckCount = 0;
		state.EventRecordsAwaitingRestore = {};
		state.EventRecordsAwaitingRestoreCount = 0;
		state.EventRecordsWithFiles = [];

		state.EventNoPermission = false;
		state.EventRecordsNoFilterRenderIndex = 0;

		state.EventRecordFilters.forEach((filter: EventRecordFilter) => {
			if (filter.showNew) {
				filter.newCount = 0;
			}
			filter.renderIndex = 0;
			filter.filterRecords = [];
		});
	},
	setEventRecordsErrorState(state, { unAckedEventRecords, unRestoredEventRecords }: { unAckedEventRecords: number[] | null, unRestoredEventRecords: number[] | null }) {

		let recordsAwaitingAck = state.EventRecordsAwaitingAck ?? {};
		let recordsAwaitingRestore = state.EventRecordsAwaitingRestore ?? {};

		state.EventRecords.filter(r => r.eventRecordTypeID == EventRecordTypes.Alarm && (!r.awaitingRestore && unRestoredEventRecords?.includes(r.eventRecordID))
								|| (!r.awaitingAcknowledgement && unAckedEventRecords?.includes(r.eventRecordID)))
			.forEach(unRestoredOrUnAcked => {
				if (unAckedEventRecords?.includes(unRestoredOrUnAcked.eventRecordID))
				{
					unRestoredOrUnAcked.awaitingAcknowledgement = true;
					recordsAwaitingAck[unRestoredOrUnAcked.eventRecordID.toString()] = unRestoredOrUnAcked;
				}

				if (unRestoredEventRecords?.includes(unRestoredOrUnAcked.eventRecordID))
				{
					unRestoredOrUnAcked.awaitingRestore = true;
					recordsAwaitingRestore[unRestoredOrUnAcked.eventRecordID.toString()] = unRestoredOrUnAcked;
				}
			});

		state.EventRecordsAwaitingAck = recordsAwaitingAck;
		state.EventRecordsAwaitingAckCount = Object.entries(state.EventRecordsAwaitingAck).length;

		state.EventRecordsAwaitingRestore = recordsAwaitingRestore;
		state.EventRecordsAwaitingRestoreCount = Object.entries(state.EventRecordsAwaitingRestore).length;
	},
    setEventRecordFilters(state, filters) {
        state.EventRecordFilters = filters;
    },
	setEventRecordFilter(state, filterIndex: number) {
		state.EventRecordsFilterActiveIndex = filterIndex;
		const filter = state.EventRecordFilters[filterIndex];
		if (filter != null && filter.clearNewOnView) {
			filter.newCount = 0;
		}
	},
	setEventRecordFilterRenderIndex(state, { filterIndex, renderIndex }) {
		state.EventRecordFilters[filterIndex].renderIndex = renderIndex;
	},
	setEventRecordsNoFilterRenderIndex(state, renderIndex: number) {
		state.EventRecordsNoFilterRenderIndex = renderIndex;
	},
	async setEventRecords(state, eventRecords: EventRecord[]): Promise<void> {
		if (eventRecords.length > 0) {
			const filters = state.EventRecordFilters;

			const currentEventRecords = state.EventRecords;
			const activeResponseIDs = state.ActiveResponseIDs;

			let recordsAwaitingRestore = state.EventRecordsAwaitingRestore ?? {};
			let recordsAwaitingAck = state.EventRecordsAwaitingAck ?? {};
			let newAdhocLocationEventRecords = [];

			const addedFilterCounts = {};
			filters.forEach((filter: any) => {
				addedFilterCounts[filter.filterIndex.toString()] = {
					count: 0,
					records: []
				};
			});

			for (let i = 0; i < eventRecords.length; i++) {
				const eventRecord = eventRecords[i];
				eventRecord.selected = false;
				eventRecord.expanded = false;
				eventRecord.camerasExpanded = false;
				eventRecord.extraDetailsExpanded = false;

				if (eventRecord.eventRecordTypeID === EventRecordTypes.Alarm) {
					if (eventRecord.awaitingAcknowledgement) {
						recordsAwaitingAck[eventRecord.eventRecordID.toString()] = eventRecord;
					}

					if (eventRecord.awaitingRestore != null) {
						recordsAwaitingRestore[eventRecord.eventRecordID.toString()] = eventRecord;
						Vue.set(eventRecord, "restoredAt", null);
					}

					if (eventRecord.location !== "" && eventRecord.eventRecordID != eventRecords[0].eventRecordID) {
						newAdhocLocationEventRecords.push(eventRecord);
					}

					if (eventRecord.details != null) {
						// Detect if the alarm has been identified as a runaway
						eventRecord.isRunaway = eventRecord.details.toUpperCase().startsWith("RUNAWAY");
					}
				}

				if (eventRecord.eventRecordTypeID === EventRecordTypes.AlarmHandlingEvent && eventRecord.objectID !== null) {
					if (recordsAwaitingAck[eventRecord.objectID.toString()] != null) {
						recordsAwaitingAck[eventRecord.objectID.toString()].awaitingAcknowledgement = false;
						delete recordsAwaitingAck[eventRecord.objectID.toString()];
					}
				}

				if(eventRecord.eventRecordTypeID === EventRecordTypes.AcknowledgeAll) {
					recordsAwaitingAck = {};
					state.AcknowledgeAllEventRecordId = eventRecord.eventRecordID;
					const lastAckAllEventRecordID = eventRecord.objectID && eventRecord.objectID !== eventRecord.eventID
														? eventRecord.objectID
														: eventRecord.eventRecordID;

					if (lastAckAllEventRecordID > (state.LastAckAllEventRecordID ?? 0))
					{
						state.LastAckAllEventRecordID = lastAckAllEventRecordID;
					}
				}

				if (
					eventRecord.eventRecordTypeID === EventRecordTypes.AttachedFile &&
					eventRecord.objectID !== null
				) {
					if (
						!state.EventRecordsWithFiles.includes(
							eventRecord.objectID
						)
					) {
						state.EventRecordsWithFiles.push(eventRecord.objectID);
					}
				} else if (
					/* Accounts for uploading a file using the older Audit method called 'upload' */
					eventRecord.eventRecordTypeID === EventRecordTypes.AttachedFile &&
					eventRecord.objectID === null &&
					eventRecord.details
						.toLocaleLowerCase()
						.startsWith("attached file:")
				) {
					state.EventRecordsWithFiles.push(eventRecord.eventRecordID);
				}

				if (eventRecord.eventRecordTypeID === EventRecordTypes.SnapShot || eventRecord.eventRecordTypeID === EventRecordTypes.RecordedVideoSnapShot) {
					if (!state.EventRecordsWithFiles.includes(eventRecord.eventRecordID)) {
						state.EventRecordsWithFiles.push(eventRecord.eventRecordID);
					}
				}

				if (
					(eventRecord.eventRecordTypeID === EventRecordTypes.AlarmRestored || eventRecord.eventRecordTypeID === EventRecordTypes.ForceRestore) &&
					eventRecord.objectID !== null
				) {
					if (recordsAwaitingRestore[eventRecord.objectID.toString()] != null) {
						recordsAwaitingRestore[eventRecord.objectID.toString()].awaitingRestore = false;
						recordsAwaitingRestore[
							eventRecord.objectID.toString()
						].restoredAt = dateStringWithZeroHourOffset(eventRecord.created);

						delete recordsAwaitingRestore[eventRecord.objectID.toString()];
					}
				}

				if (eventRecord.eventRecordTypeID === EventRecordTypes.ForceRestoreAllAlarms) {
					const restoredAt = dateStringWithZeroHourOffset(eventRecord.created);
					state.restoreAllEventRecordId = eventRecord.eventRecordID;
					const lastRestoreAllEventRecordID = eventRecord.objectID && eventRecord.objectID !== eventRecord.eventID
														? eventRecord.objectID
														: eventRecord.eventRecordID;

					Object.keys(recordsAwaitingRestore).forEach(eventRecordID => {
						if (recordsAwaitingRestore[eventRecordID].eventRecordID <= lastRestoreAllEventRecordID) {
							recordsAwaitingRestore[eventRecordID].awaitingRestore = false;
							recordsAwaitingRestore[eventRecordID].restoredAt = restoredAt;

							delete recordsAwaitingRestore[eventRecordID];
						}
					});

					if (lastRestoreAllEventRecordID > (state.lastRestoreAllEventRecordId ?? 0))
					{
						state.lastRestoreAllEventRecordId = lastRestoreAllEventRecordID;
					}
				}

				// Set eventCaseId when the event has been raised
				if (eventRecord.eventRecordTypeID === EventRecordTypes.CaseCreated && !state.eventCaseId) {
					try
					{
						// We want everything after the first '#', e.g.
						// "Raised to Case Management - Record Id: #123" -> "123"
						const regex: RegExp = /#([\s\S]*)/;
						const caseRecordId = regex.exec(eventRecord.details)[1];
						state.eventCaseId = caseRecordId;
					}
					catch(ex)
					{
						console.error(ex);
					}
				}

				filters.forEach((filter: EventRecordFilter) => {
					if (filter.filterRecordIds.indexOf(eventRecord.eventRecordTypeID) > -1) {
						if (filter.showNew) {
							addedFilterCounts[filter.filterIndex.toString()].count++;
						}

						addedFilterCounts[filter.filterIndex.toString()].records.push(eventRecord);
					}
				});
			}

			filters.forEach((filter: EventRecordFilter) => {
				if (filter.showNew) {
					filter.newCount += addedFilterCounts[filter.filterIndex.toString()].count;
				}

				filter.filterRecords.push(...addedFilterCounts[filter.filterIndex.toString()].records);
			});

			currentEventRecords.push(...eventRecords);

			// Ensure that event records that are still awaiting acknowledge are shown
			currentEventRecords.filter(r => r.eventRecordTypeID == EventRecordTypes.Alarm && r.awaitingAcknowledgement)
				.forEach(r =>
					{
						r.awaitingAcknowledgement = r.eventRecordID > state.LastAckAllEventRecordID;
						if (r.awaitingAcknowledgement)
						{
							recordsAwaitingAck[r.eventRecordID.toString()] = r;
						}
						else
						{
							delete recordsAwaitingAck[r.eventRecordID.toString()];
						}
					});

			state.EventRecordsAwaitingAck = recordsAwaitingAck;

			const recordsAwaitingAckCount = Object.keys(recordsAwaitingAck).length;
			if (state.EventDetails?.eventRecordCount >= largeEventSize || recordsAwaitingAckCount == 0){
				try
				{
					state.EventRecordsAwaitingAckCount = await api.retrieveAwaitingAckCountForEvent(state.EventDetails.eventID);
				}
				catch(err)
				{
					console.error(`Failed to retrieve ack count for event ${state.EventDetails?.eventID} ex: ${err}`)
					state.EventRecordsAwaitingAckCount = recordsAwaitingAckCount;
				}
			}
			else
			{
				state.EventRecordsAwaitingAckCount = recordsAwaitingAckCount;
			}

			let eventRecordsAwaitingRestoreCount = 0;
			for (const eventRecordID in recordsAwaitingRestore) {
				if (recordsAwaitingRestore[eventRecordID].awaitingRestore) {
					eventRecordsAwaitingRestoreCount++;
				}
			}

			// update adhoc location event records if any have been found
			if (newAdhocLocationEventRecords.length > 0) {
				state.AdhocLocationEventRecords = state.AdhocLocationEventRecords.concat(newAdhocLocationEventRecords);
			}

			state.EventRecordsAwaitingRestoreCount = eventRecordsAwaitingRestoreCount;
			state.EventRecordsAwaitingRestore = recordsAwaitingRestore;

			state.ActiveResponseIDs = activeResponseIDs;
			state.EventRecords = currentEventRecords;
		}
	},
	updateFirstEventRecordLocation(state, location: string)
	{
		state.EventRecords[0].location = location;
		state.EventRecords[0].latLong = location;
	},
	async setEventAckCount(state): Promise<void> {
		state.EventRecordsAwaitingAckCount = await api.retrieveAwaitingAckCountForEvent(state.EventDetails.eventID);
	},
	setLatestEventRecordID(state, eventRecordID: number) {
		state.LatestEventRecordID = eventRecordID;
	},
	setEventDetails(state, eventDetails: any | null) {
		state.EventDetails = eventDetails;

		if (eventDetails == null) {
			state.IsController = false;
			state.auditService = null;
			state.GroupID = null;
			state.EventRecordsFilterActiveIndex = -1;
			state.activeMapItems = null;
			state.EventRecords = [];
			state.EventRecordsNoFilterRenderIndex = 0;
			state.SelectedEventRecord = null;
			state.EventRecordFilters.forEach((filter: any) => {
				if (filter.showNew) {
					filter.newCount = 0;
				}
				filter.renderIndex = 0;
				filter.filterRecords = [];
			});

			state.LastAckAllEventRecordID = 0;
			state.LatestEventRecordID = 0;
			state.LastActivity = null;
		} else {
			state.auditService = new AuditService(api.getAuditServiceAddress(), eventDetails?.auditEndpoint);

			state.GroupID = eventDetails.groupID;
			state.LastActivity = new Date().valueOf();

			state.EventRecordsFilterActiveIndex = eventDetails.eventTypeID === 1 ? 0 : -1;
		}

		state.eventLocationLastSet = new Date().valueOf();
	},
	setEventRecordExpanded(state, { eventRecordID, expanded }: any) {
		state.EventRecords.find(e => e.eventRecordID == eventRecordID).expanded = expanded;
	},
	setEventRecordEventRecordDetails(state, { eventRecordID, EventRecordDetails }: any) {
		state.EventRecords.find(e => e.eventRecordID == eventRecordID).EventRecordDetails = EventRecordDetails;
	},
	setGroupID(state, groupID: number) {
		state.GroupID = groupID;
	},
	setEventCloseShown(state, shown: boolean) {
		state.EventCloseOpen = shown;
	},
	setEventRaiseShown(state, shown: boolean) {
		state.EventRaiseOpen = shown;
	},
	setIncidentReportShown(state, shown: boolean) {
		state.IncidentReportOpen = shown;
	},
	setIncidentReportDownloaded(state, downloaded: boolean) {
		state.IncidentReportDownloaded = downloaded;
	},
	setEventDetailsShown(state, shown: boolean) {
		state.EventDetailsOpen = shown;
		state.LastActivity = new Date().valueOf();
	},
	setPutEventOnTestShown(state, shown: boolean) {
		state.PutEventOnTestOpen = shown;
	},
	setEventShare(state, sharing: any[]) {
		state.EventShares = sharing;
	},
	setMobileEventShares(state, sharing: any[]) {
		state.MobileEventShares = sharing;
	},
	setAdHocShareDetails(state, { eventUserID, shareLink, accessCode }: any) {
		state.AdhocShareDetails[eventUserID] = {
			shareLink,
			accessCode
		};
	},
	setShareWithPersons(state, data: ShareWithPerson[]) {
		state.ShareWithPersons = data;
	},
	setEventShareShown(state, shown: boolean) {
		state.EventSharingOpen = shown;
	},
	setInvolvedPartiesShown(state, shown: boolean) {
		state.InvolvedPartyOpen = shown;
	},
	setUserID(state, UserID: number) {
		state.UserID = UserID;
	},
	setСontrollerUserID(state, id: number) {
		state.ControllerUserID = id;
	},
	setIsСontroller(state, isController: boolean) {
		state.IsController = isController;
	},
	setEventLeaveShown(state, shown: boolean) {
		state.EventLeaveOpen = shown;
	},
	setEventLeaveNote(state, note: string | null) {
		state.EventLeaveNote = note;
	},
	setExternalUsersAllowed(state, allow: boolean) {
		state.ExternalUsersAllowed = allow;
	},
	setForceRestoreModalData(state, data: ForceRestoreModalData) {
		state.forceRestoreModalData = data;
	},
	/* setUserShares(state, userShares: any) {
    state.userShares = userShares;
  	}, */
	setEventSharesSet(state, set) {
		state.EventSharesSet = set;
	},
	setRequestingEventRecords(state, requesting: any) {
		state.requestingEventRecords = requesting;
	},
	setSelectedEventRecord(state, eventRecord: any) {
		if (state.SelectedEventRecord != null) {
			state.SelectedEventRecord.selected = false;
		}

		state.SelectedEventRecord = eventRecord;
		if (eventRecord != null) {
			state.SelectedEventRecord!.selected = true;
		}
	},
	setGoToLocation(state, location: string) {
		state.goToLocation = location;
	},
	setLocation(state, data: any) {
		state.EventDetails!.latLong = data.latLong;
		state.EventDetails!.minElevation = data.minElevation;
		state.EventDetails!.maxElevation = data.maxElevation;

		state.eventLocationLastSet = new Date().valueOf();
	},
	setActiveMapItems(state, data: any) {
		state.activeMapItems = data;
	},
	setEventNoPermission(state, noPermission: boolean) {
		state.EventNoPermission = noPermission;
	},
	setMapCircleCenter(state, mapCircleCenter: string) {
		state.mapCircleCenter = mapCircleCenter;
	},
	setEventRecordShares(state, userShares) {
		state.EventRecords.forEach(eventRecord => {
			if (eventRecord.eventUserID != null) {
				const userShare = userShares[eventRecord.eventUserID];

				if (userShare) {
					eventRecord.colorIndex = userShare.colorIndex;
					eventRecord.initials = userShare.initials;
					eventRecord.userName = userShare.name;
				} else {
					eventRecord.colorIndex = null;
					eventRecord.initials = null;
					eventRecord.userName = null;
				}
			}
		});
	},
	setMaskReasons(state, data: MaskReason[]) {
		state.MaskReasons = data;
	},
	setGroupMaskReasons(state, data: MaskReason[]) {
		state.GroupMaskReasons = data;
	},
	setMaskedAlarms(state, data: MaskedAlarm[]) {
		state.MaskedAlarms = data;
	},
	setActivity(state, date) {
		state.LastActivity = date || new Date().valueOf();
	},
	setLastActivity(state, activityAt) {
		state.LastActivity = activityAt;
	},
	setActiveMapItemsRequired(state, required: boolean) {
		state.ActiveMapItemsRequired = required;
	},
	setEventRecordResponse(state, { eventRecordID, title, priority, responseID }: any) {
		const recordToUpdate = state.EventRecords.find(er => er.eventRecordID === eventRecordID);

		recordToUpdate.details = title;
		recordToUpdate.responsePriority = priority;
		recordToUpdate.objectID = responseID;
	},
	ackEventRecord(state, eventRecordID: number) {
		state.LastActivity = new Date().valueOf();
		const recordToUpdate = state.EventRecords.find(er => er.eventRecordID === eventRecordID);
		recordToUpdate.awaitingAcknowledgement = false;
	},

	/**
	 * Sets the FlaggedForReview property for the current event (EventDetails).
	 * @param state The vuex state.
	 * @param flagged Whether or not the event has been flagged for review.
	 */
	setFlaggedForReview(state, flagged: boolean) {
		state.EventDetails.flaggedForReview = flagged;
	},

	setIsUploadingFile(state, uploading: boolean) {
		state.IsUploadingFile = uploading;
	},
	resetState(state) {
		Object.assign(state, getDefaultState());
	},
	setHideMapFlag(state, hideMapFlag: boolean) {
		state.LastActivity = new Date().valueOf();
		state.hideMapFlag = hideMapFlag;
	},
	setApplianceServerDetails(state, applianceServers: ApplianceServerDetails[]) {
		state.applianceServerDetails = applianceServers;
	},
	setDeviceServerDetails(state, deviceServerDetails: DeviceServerDetails[]) {
		var ids = new Set(deviceServerDetails.map(dev => dev.deviceId));

		// Take new over the existing as there may have been an update.
		state.deviceServerDetails = [...deviceServerDetails, ...state.deviceServerDetails.filter(dev => !ids.has(dev.deviceId))];
    },
	setApplianceServerAndDeviceServerPollingStatus(state, pollingStatus: PollingStatus) {
		state.applianceServerAndDeviceServerPollingStatus = pollingStatus;
	},
	addAppliancePollingConsumer(state, source: string) {
		if (!state.appliancePollingConsumers.includes(source)) {
			state.appliancePollingConsumers = [...state.appliancePollingConsumers, source];
		}
	},
	removeAppliancePollingConsumer(state, source: string) {
		state.appliancePollingConsumers = state.appliancePollingConsumers.filter(consumer => consumer !== source);
	},
	clearAppliancePollingConsumers(state) {
		state.appliancePollingConsumers = [];
	},
	setEventCaseId(state, id: string) {
		state.eventCaseId = id;
	},
	setAuditService(state) {
		state.auditService = new AuditService(api.getAuditServiceAddress());
	},

	setEventOutcomes(state, eventOutcomeResponse: EventOutcomeResponse) {
		state.eventOutcomes = eventOutcomeResponse;
	},
	setAreaDetailsTabIndex(state, tab: number) {
		state.areaDetailsTabIndex = tab;
	},
	setEventRecordExtraDetailsExpanded(state, { eventRecordId, expanded }: ExtraDetailsExpandedState) {
		state.EventRecords.find(e => e.eventRecordID == eventRecordId).extraDetailsExpanded = expanded;
	},
	resetAcknowledgeAllEventRecordId(state): void {
		state.AcknowledgeAllEventRecordId = null;
	},
	setNotifyNotificationRules(state, notifyNotificationRules: NotifyNotificationRules | null) {
		state.notifyNotificationRules = notifyNotificationRules;
	},
	setInvolvedParties(state, involvedParties: InvolvedParty[]) {
		state.involvedParties = involvedParties;
	},
	setNotificationRulesContributingToNotification(state, notificationRules: NotificationRule[]) {
		state.notificationRulesContributingToNotification = notificationRules;
	},
	resetSiteMonitorState(state) {
		Object.assign(state, getDefaultState());
	}
};
